import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { Menu, X, Globe } from "lucide-react";

export default function Header() {
  const { t, i18n } = useTranslation();
  const { lang = "ar" } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const nav = [
    { key: "home", href: `/${lang}` },
    { key: "sector", href: `/${lang}#why` },
    { key: "circuit", href: `/${lang}#circuit` },
    { key: "transparency", href: `/${lang}#transparency` },
  ];

  const switchLang = (target) => {
    if (target === lang) return;
    if (i18n.language !== target) i18n.changeLanguage(target);
    // Mark language switch so ScrollToTop skips scrolling
    window.__zx_langSwitch = true;
    // Replace only the language segment in the current pathname
    const parts = location.pathname.split("/").filter(Boolean);
    if (parts[0] === "ar" || parts[0] === "en") {
      parts[0] = target;
    } else {
      parts.unshift(target);
    }
    navigate("/" + parts.join("/") + location.search + location.hash, { replace: false });
    setOpen(false);
  };

  const scrollToHash = (href) => {
    setOpen(false);
    const [pathPart, hash] = href.split("#");
    if (location.pathname === pathPart) {
      if (hash) {
        const el = document.getElementById(hash);
        if (el) el.scrollIntoView({ behavior: "auto", block: "start" });
        window.history.replaceState(null, "", `${pathPart}#${hash}`);
      } else {
        window.scrollTo(0, 0);
        window.history.replaceState(null, "", pathPart);
      }
    } else {
      // Navigate to home; Home component will handle scrolling to the target block after mount
      navigate(href);
    }
  };

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 w-full border-b border-slate-700/40 bg-[#0A2540] text-white"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-10">
        <Link
          to={`/${lang}`}
          data-testid="header-logo-link"
          className="flex items-center gap-2"
        >
          <span className="flex h-8 w-8 items-center justify-center rounded-sm bg-[#C85A32] font-mono text-sm font-bold text-white">
            Z
          </span>
          <span className="font-serif text-xl font-bold tracking-tight text-white">
            Zynqelix
          </span>
        </Link>

        <nav className="hidden lg:flex lg:items-center lg:gap-8">
          {nav.map((n) => (
            <button
              key={n.key}
              type="button"
              onClick={() => scrollToHash(n.href)}
              data-testid={`nav-link-${n.key}`}
              className="text-sm font-medium text-slate-200 hover:text-[#C85A32]"
            >
              {t(`nav.${n.key}`)}
            </button>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <div className="flex items-center gap-1 rounded-md border border-slate-600 bg-[#0E3358] p-0.5 text-xs">
            <button
              type="button"
              onClick={() => switchLang("ar")}
              data-testid="lang-switch-ar"
              className={`rounded px-2 py-1 font-semibold ${lang === "ar" ? "bg-white text-[#0A2540]" : "text-slate-200"}`}
              aria-label="Español"
            >
              ES
            </button>
            <button
              type="button"
              onClick={() => switchLang("en")}
              data-testid="lang-switch-en"
              className={`rounded px-2 py-1 font-semibold ${lang === "en" ? "bg-white text-[#0A2540]" : "text-slate-200"}`}
              aria-label="English"
            >
              EN
            </button>
          </div>
          <button
            type="button"
            onClick={() => scrollToHash(`/${lang}#newsletter`)}
            data-testid="header-cta-btn"
            className="rounded-md bg-[#C85A32] px-4 py-2 text-sm font-semibold text-white hover:bg-[#B04D28]"
          >
            {t("nav.cta")}
          </button>
        </div>

        <button
          type="button"
          className="inline-flex items-center justify-center rounded-md p-2 text-white lg:hidden"
          onClick={() => setOpen((v) => !v)}
          data-testid="header-mobile-toggle"
          aria-label="Menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden" data-testid="header-mobile-menu">
          <div className="space-y-1 border-t border-slate-700/40 bg-[#0A2540] px-4 pb-6 pt-2 sm:px-6">
            {nav.map((n) => (
              <button
                key={n.key}
                type="button"
                onClick={() => scrollToHash(n.href)}
                data-testid={`mobile-nav-link-${n.key}`}
                className="block w-full rounded-md px-3 py-3 text-left text-base font-medium text-slate-100 hover:bg-[#0E3358]"
              >
                {t(`nav.${n.key}`)}
              </button>
            ))}
            <div className="mt-4 flex items-center gap-2 border-t border-slate-700/40 pt-4">
              <Globe size={16} className="text-slate-300" />
              <span className="text-xs uppercase tracking-widest text-slate-400">
                {t("lang.label")}
              </span>
              <div className="ml-auto flex items-center gap-1 rounded-md border border-slate-600 bg-[#0E3358] p-0.5 text-xs">
                <button
                  type="button"
                  onClick={() => switchLang("ar")}
                  data-testid="mobile-lang-switch-ar"
                  className={`rounded px-2 py-1 font-semibold ${lang === "ar" ? "bg-white text-[#0A2540]" : "text-slate-200"}`}
                >
                  ES
                </button>
                <button
                  type="button"
                  onClick={() => switchLang("en")}
                  data-testid="mobile-lang-switch-en"
                  className={`rounded px-2 py-1 font-semibold ${lang === "en" ? "bg-white text-[#0A2540]" : "text-slate-200"}`}
                >
                  EN
                </button>
              </div>
            </div>
            <button
              type="button"
              onClick={() => scrollToHash(`/${lang}#newsletter`)}
              data-testid="mobile-header-cta-btn"
              className="mt-3 block w-full rounded-md bg-[#C85A32] px-4 py-3 text-center text-sm font-semibold text-white"
            >
              {t("nav.cta")}
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
